import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage, LanguageToggle } from '../context/LanguageContext';
import { ArrowLeft, AlertCircle, Building2, Mail, UserPlus } from 'lucide-react';
import OrbitLogo from '../assets/OrbitLogo.png';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

export default function Login() {
  const [flow, setFlow] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [regForm, setRegForm] = useState({ full_name: '', business_name: '', email: '', password: '', confirm: '' });
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const utmCampaign = new URLSearchParams(window.location.search).get('utm_campaign') || '';
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  React.useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    if (regForm.password !== regForm.confirm) return setRegError('Passwords do not match');
    if (regForm.password.length < 8) return setRegError('Password must be at least 8 characters');
    setRegLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: regForm.email, full_name: regForm.full_name, business_name: regForm.business_name, password: regForm.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Registration failed');
      await login(regForm.email, regForm.password);
      navigate('/');
    } catch (err) {
      setRegError(err.message);
    } finally {
      setRegLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, utmCampaign);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!flow) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 relative">
        <div className="absolute top-5 right-5 z-10">
          <LanguageToggle />
        </div>
        <div className="w-full max-w-lg">
          <div className="text-center mb-12">
            <img src={OrbitLogo} alt="Capital Infusion Orbit" className="h-[960px] w-auto mx-auto mb-3 object-contain" />
          </div>

          <div className="grid grid-cols-1 gap-4 max-w-xs mx-auto">
            <button
              onClick={() => setFlow('client')}
              className="group bg-white border border-gray-200 hover:border-blue-300 rounded-2xl p-7 text-left transition-all hover:shadow-md"
            >
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-5 group-hover:bg-blue-600 transition-colors">
                <Building2 size={18} className="text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="text-gray-900 font-semibold text-base mb-1.5">{t('Client Login')}</h2>
              <p className="text-gray-400 text-sm leading-relaxed">{t('Access your application, upload documents, and track funding status.')}</p>
              <p className="text-blue-600 text-sm font-medium mt-5">{t('Continue')} →</p>
            </button>
          </div>

          <p className="text-center mt-6">
            <button onClick={() => setShowRegister(true)} className="text-gray-400 text-xs hover:text-gray-600 transition-colors inline-flex items-center gap-1">
              <UserPlus size={11} /> New client? Create an account
            </button>
          </p>

          <p className="text-center text-gray-300 text-xs mt-4">Capital Infusion · Inc 5000 Company · Encrypted Software</p>

          {installPrompt ? (
            <button onClick={handleInstall}
              className="w-full mt-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-xl flex items-center justify-center gap-2 transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
              Download Mobile App
            </button>
          ) : isMobile && (
            <button onClick={() => {
              if (/iPhone|iPad/i.test(navigator.userAgent)) {
                alert('To install Orbit: tap the Share button (box with arrow) at the bottom of Safari, then tap "Add to Home Screen"');
              } else {
                alert('To install Orbit: tap the browser menu (⋮) and select "Add to Home Screen" or "Install App"');
              }
            }} className="w-full mt-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-xl flex items-center justify-center gap-2 transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
              Download Mobile App
            </button>
          )}
        </div>
      </div>
    );
  }

  const isClient = flow === 'client';

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <button onClick={() => { setFlow(null); setError(''); }} className="flex items-center gap-1.5 text-blue-600 text-sm mb-8 hover:opacity-70 transition-opacity">
          <ArrowLeft size={14} /> Back
        </button>

        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <div className="text-center mb-7">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl mb-4 bg-blue-50">
              <Building2 size={20} className="text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">
              {isClient ? 'Client Portal' : 'Operations Portal'}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {isClient ? 'Sign in to manage your application' : 'Sign in to access your workspace'}
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl px-4 py-3 mb-5">
              <AlertCircle size={14} className="shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Email</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Password</label>
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)} required
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className={`w-full py-2.5 rounded-xl font-medium text-sm text-white transition-all disabled:opacity-50 ${
                isClient ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-900 hover:bg-gray-800'
              }`}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            {isClient && (
              <button
                type="button"
                onClick={() => setForgotMode(true)}
                className="w-full text-center text-sm text-blue-600 hover:opacity-70 mt-2"
              >
                Forgot password?
              </button>
            )}
          </form>
        </div>

        {/* Create Account link under sign-in form */}
        <p className="text-center mt-4">
          <button onClick={() => { setFlow(null); setShowRegister(true); }} className="text-gray-400 text-xs hover:text-gray-600 transition-colors inline-flex items-center gap-1">
            <UserPlus size={11} /> New client? Create an account
          </button>
        </p>

        {/* Forgot Password Modal */}
        {/* Register Modal */}
        {showRegister && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 w-full max-w-sm">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-blue-50 rounded-2xl mb-4">
                  <UserPlus size={20} className="text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Create Account</h2>
                <p className="text-gray-400 text-sm mt-1">Set up your client portal access</p>
              </div>
              {regError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl px-4 py-3 mb-4">
                  <AlertCircle size={14} className="shrink-0" /> {regError}
                </div>
              )}
              <form onSubmit={handleRegister} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Full Name</label>
                  <input type="text" required value={regForm.full_name}
                    onChange={e => setRegForm(f => ({ ...f, full_name: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    placeholder="John Smith" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Business Name</label>
                  <input type="text" value={regForm.business_name}
                    onChange={e => setRegForm(f => ({ ...f, business_name: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    placeholder="Acme LLC" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Email</label>
                  <input type="email" required value={regForm.email}
                    onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    placeholder="you@business.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Password</label>
                  <input type="password" required value={regForm.password}
                    onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    placeholder="Min 8 characters" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Confirm Password</label>
                  <input type="password" required value={regForm.confirm}
                    onChange={e => setRegForm(f => ({ ...f, confirm: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    placeholder="••••••••" />
                </div>
                <button type="submit" disabled={regLoading}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-xl disabled:opacity-50 mt-1">
                  {regLoading ? 'Creating account...' : 'Create Account'}
                </button>
                <button type="button" onClick={() => { setShowRegister(false); setRegError(''); }}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-700">
                  Back to login
                </button>
              </form>
            </div>
          </div>
        )}

        {forgotMode && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 w-full max-w-sm">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-11 h-11 bg-blue-50 rounded-2xl mb-4">
                  <Mail size={20} className="text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Reset Password</h2>
                <p className="text-gray-400 text-sm mt-1">Enter your email and we'll send a reset link</p>
              </div>
              {forgotMsg && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-100 text-green-700 text-sm rounded-xl px-4 py-3 mb-4">
                  {forgotMsg}
                </div>
              )}
              <form onSubmit={async (e) => {
                e.preventDefault();
                setForgotLoading(true);
                try {
                  const API = process.env.REACT_APP_API_URL || 'http://capital-infusion-api-prod.eba-wqytrheg.us-east-1.elasticbeanstalk.com/api';
                  await fetch(`${API}/auth/forgot-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: forgotEmail }),
                  });
                } catch {}
                setForgotMsg('If an account exists with that email, a reset link has been sent.');
                setForgotLoading(false);
              }} className="space-y-4">
                <input
                  type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  placeholder="you@example.com"
                />
                <button type="submit" disabled={forgotLoading}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-xl disabled:opacity-50"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => { setForgotMode(false); setForgotMsg(''); }}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
                >
                  Back to login
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
